<template>
  <main id="home" class="site-main" :style="cssVars">
    <div class="grid-x grid-padding-x">
      <div class="cell">
        <div class="grid-x grid-padding-x">
          <div class="welcome cell large-auto">
            <div class="welcome__title">{{ welcomeMessage }},
              {{ shop ? shop.name : '' }}
            </div>

            <div class="welcome__content">
              <p>The new Visceral Consignment is coming! Request early access in the notifications section.</p>
              <div v-if="shop.scope_update_required">
                <a class="button button--small" style="margin-bottom:5px;" :href="shop.scope_update_auth_url" target="_blank">SCOPE UPDATE REQUIRED</a>
              </div>
            </div>
          </div>

          <div class="stats cell large-shrink">
            <div>
              <div class="grid-x">
                <div class="cell auto label" v-if="shop.myshopify_domain == 'tiny-brambles.myshopify.com'">
                  <div class="label__heading">Profit</div>
                  <div class="label__content">{{ storeProfit }}</div>
                </div>

                <div class="cell auto label warning">
                  <div class="label__heading">Due</div>
                  <div class="label__content">{{ commissionDue }}</div>
                </div>

                <div class="cell auto label success">
                  <div class="label__heading">Paid</div>
                  <div class="label__content">{{ commissionPaid }}</div>
                </div>

                <div class="cell auto label">
                  <div class="label__heading">Earned</div>
                  <div class="label__content">{{ commissionEarned }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="grid-x grid-margin-x">
          <div class="panel cell large-8 xlarge-6 small-order-1 show-for-large">
            <chart></chart>
          </div>

          <div
            class="panel cell small-auto medium-6 xlarge-3 small-order-3 medium-order-3 xlarge-2"
          >
            <div class="panel__header">
              <h3 class="panel__title">Recent Payouts</h3>
            </div>

            <recentPayouts></recentPayouts>
          </div>

          <div
            class="panel cell small-auto large-4 xlarge-3 small-order-2 xlarge-order-3">

            <div v-if="shop.agree_to_upgrade && shop.upgrade_process_step > 0">
              <MigrationProgress :shop="shop" @activate="enableNewApp" />
            </div>
            <div v-else-if="shop.show_upgrade_offer">
              <div v-if="!shop.agree_to_upgrade">
                <a href="#" @click="showModal = true"><img src="../../assets/img/LimitedOfferBannerAd.png" alt="" width="300" class="exclusiveOffer"></a>
              </div>
              <div v-else class="upgrade-locked-notice">
                <div class="locked-offer-container">
                  <img src="../../assets/img/LimitedOfferLockedIn.png" alt="" width="300" class="exclusiveOfferLockedIn">
                  <div class="locked-offer-text">
                    <p><strong>Acceptance Date:</strong> {{ formatDate(shop.agree_to_upgrade_datetime) }}</p>
                    <p>We'll contact you soon to begin the migration process.</p>
                  </div>
                </div>
              </div>
            </div>
            <div v-else>
              <div class="panel__header" style="margin-bottom: 0;">
                <div class="grid-x grid-padding-x">
                  <div class="cell auto">
                    <h3 class="panel__title">Notifications</h3>
                  </div>
                </div>
              </div>
              <div class="panel__content">
                <div class="newVersionNotice">
                  <p class="heading"><i class="fas fa-rocket-launch"></i> The New Visceral Consignment is Coming!</p>
                  <p>Visceral Consignment has been rebuilt from the ground up — faster, smarter, and ready to scale with your business. With powerful new tools like customizable commission calculations, you can be among the first to try it.</p>
                  <p><a class="button button--small" href="#" @click="showEarlyAccessModal = true">Request early access</a></p>
                </div>
                <announcements></announcements>
              </div>
            </div>
          </div>

          <div
            class="panel cell medium-6 xlarge-4 small-order-2 medium-order-4">
            <div class="panel__header">
              <h3 class="panel__title">Commissions Due</h3>
            </div>

            <div class="panel__content">
              <commissionsDue></commissionsDue>
            </div>
          </div>

          <div class="panel cell xlarge-8 small-order-5">
            <topEarners></topEarners>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal for Limited Time Upgrade Offer -->
    <UpgradeModal :show="showModal" :shop="shop" @close="showModal = false" />

    <!-- Modal for Early Access Request -->
    <EarlyAccessModal :show="showEarlyAccessModal" @close="showEarlyAccessModal = false" />

    <!-- Modal for App Activation Confirmation -->
    <ActivationConfirmModal
      :show="showActivationModal"
      @close="showActivationModal = false"
      @confirm="confirmActivation" />
  </main>

</template>

<script>
import client from '@/mixins/client'
import utils from '@/mixins/utils'
import chart from '@/pages/vendors/sections/chart'
import recentPayouts from '@/pages/vendors/sections/recentPayouts'
import commissionsDue from '@/pages/vendors/sections/commissionsDue'
import topEarners from '@/pages/vendors/sections/topEarners'
import announcements from '@/pages/vendors/sections/announcements'
import UpgradeModal from './components/UpgradeModal'
import EarlyAccessModal from './components/EarlyAccessModal'
import ActivationConfirmModal from './components/ActivationConfirmModal'
import MigrationProgress from './components/MigrationProgress'

export default {
  name: 'home',
  mixins: [client, utils],
  components: {
    chart,
    recentPayouts,
    commissionsDue,
    topEarners,
    announcements,
    UpgradeModal,
    EarlyAccessModal,
    ActivationConfirmModal,
    MigrationProgress
  },
  data: function () {
    return {
      welcomeMessage: 'Good Morning',
      orderCount: 0,
      commissionDue: 0,
      commissionPaid: 0,
      commissionEarned: 0,
      storeProfit: 0,
      showTutorial: false,
      showTutorial2: false,
      showModal: false,
      showEarlyAccessModal: false,
      showMigrationModal: false,
      showActivationModal: false
    }
  },
  methods: {
    enableNewApp () {
      this.showActivationModal = true
    },
    confirmActivation () {
      const shopDomain = this.shop && this.shop.myshopify_domain ? this.shop.myshopify_domain : ''

      this.request('patch', '/shopify/shops/request-activation/', {
        shop: shopDomain
      }).then(response => {
        this.$store.state.shop.activation_requested = true
        this.$store.state.shop.upgrade_process_step = 6
        this.showActivationModal = false
      }).catch(error => {
        console.error('Activation request failed:', error)
      })
    },
    getOrders: function () {
      this.request('get', '/orders/', {
        date_range: this.dateRange
      }).then((response) => {
        this.orderCount = response.count
      })
    },
    getTotals: function () {
      this.request('get', '/vendors/commission-totals/combined/').then(
        (response) => {
          this.commissionDue = this.formatMoney(response.commission_due)
          this.commissionPaid = this.formatMoney(response.commission_paid)
          this.commissionEarned = this.formatMoney(response.commission_earned)
          this.storeProfit = this.formatMoney(response.store_profit)
        }
      )
    },
    getPortalColor (key) {
      if (this.$store.state.metas && this.$store.state.metas[key] && this.$store.state.metas[key].meta_value !== '') {
        return this.$store.state.metas[key].meta_value
      }
      return false
    },
    formatDate (datetime) {
      if (!datetime) return ''
      return new Date(datetime).toLocaleDateString()
    }
  },
  computed: {
    shop () {
      return this.$store.state.shop
    },
    meta () {
      return this.$store.state.metas
    },
    primaryColor () {
      let color = '#eb008c'
      if (this.getPortalColor && this.getPortalColor('vendor_portal_primary_color') !== false) {
        color = this.getPortalColor('vendor_portal_primary_color')
      }
      return color
    },
    secondaryColor () {
      let color = '#7101a5'
      if (this.getPortalColor && this.getPortalColor('vendor_portal_secondary_color') !== false) {
        color = this.getPortalColor('vendor_portal_secondary_color')
      }
      return color
    },
    cssVars () {
      return {
        '--primary-color': this.primaryColor,
        '--secondary-color': this.secondaryColor
      }
    }
  },
  created () {
    this.commissionDue = this.formatMoney(0)
    this.commissionPaid = this.formatMoney(0)
    this.commissionEarned = this.formatMoney(0)

    this.getOrders()
    this.getTotals()

    let date = new Date()
    let hours = date.getHours()
    this.welcomeMessage = hours < 12 ? 'Good Morning' : hours < 18 ? 'Good Afternoon' : 'Good Evening'
  }
}
</script>

<style lang="scss">
@import "../../styles/mixins";
@import "../../styles/variables";

.welcome {
  &__title {
    font-size: 24px;
    font-weight: 900;
  }

  &__content {
    /*max-width: 600px;*/
  }
}

.newVersionNotice {
  background-color: #ebdaf1;
  border-radius: 5px;
  border: 1px solid #d0c3d5;
  padding: 10px 20px;
  margin: 10px 0;
  .heading {
    font-weight: bold;
    font-size: 16px;
  }
  p {
    font-size: 14px;
    line-height: 1.3;
  }
  .button {
    background-color: var(--secondary-color);
    &:hover,
    &:focus {
      color: #fff;
    }
  }
}

.stats {
  padding-top: 10px;

  .grid-x {
    margin: 0 -2px;
  }

  .cell {
    flex: none;
  }

  .label {
    margin: 0 2px 15px;
    padding-top: 15px;
    padding-bottom: 15px;
    text-align: left;

    &__heading {
      font-size: 16px;
      margin-bottom: 5px;
    }

    &__content {
      font-size: 18px;
      font-weight: 700;
      min-width: 180px;
    }
  }
}

@media (min-width: 641px) {
  .stats {
    padding-top: 0;

    .label {

      &__heading {
        font-size: 18px;
        margin-bottom: 5px;
      }

      &__content {
        font-size: 24px;
        font-weight: 700;
      }
    }
  }
}

.upgrade-locked-notice {
  .locked-offer-container {
    position: relative;
    display: inline-block;
  }

  .locked-offer-text {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 25%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 10px;
    color: #fff !important;
    text-align: center;

    p {
      color: #fff !important;
      margin: 2px 0;
      font-size: 12px;
      line-height: 1.2;
    }

    strong {
      color: white;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/apps/vendorconsignment/home.vue